/**
 * Centralized reunification WebSocket handlers for SMS.
 *
 * Routes every reunification event type — regardless of whether the current
 * view has an open `eventId` drawer — into React Query invalidations so every
 * reunification panel (event list, detail, students, guardians, staff,
 * metrics) auto-refreshes. The registry listens independent of the current
 * view, so which reunifications a client sees is decided purely by the
 * backend's channel-group routing.
 *
 * Canonical event contract:
 *   reunification.event.{activated,paused,resumed,completed}
 *   reunification.student.{status_changed,released,arrived,transported,offsite_reported}
 *   reunification.guardian.{arrived,checked_in,verified,matched,notified,pre_verified}
 *   reunification.staff.{assigned,checked_in,reassigned,released,status_changed,unassigned}
 *   reunification.stats.updated
 *   reunification.alert.ended   — ADR 106, fires once when originating alert ends
 *
 * The legacy bare names (`reunification.activated`, `student.status_changed`,
 * …) are registered as aliases of the canonical form, so a frame under either
 * name-space reaches the same handler.
 */

import { reunificationEventKeys } from '../../hooks/queries/useReunification';
import type {
  HandlerDeps,
  ReunificationActivatedPayload,
  SocketHandler,
  SocketHandlerMap,
} from './types';

/**
 * Every canonical reunification event type the registry routes.
 *
 * Covers the 14 lifecycle/entity events + stats, the
 * extra entity events emitted by
 *   apps/aegix-aim-backend/server/apps/reunification/constants.py
 * (student.arrived/transported/offsite_reported, guardian.notified/
 * pre_verified, staff.status_changed/unassigned), and the ADR-106
 * alert.ended transition.
 */
export const REUNIFICATION_EVENT_TYPES = [
  // Lifecycle
  'reunification.event.activated',
  'reunification.event.paused',
  'reunification.event.resumed',
  'reunification.event.completed',
  // Student
  'reunification.student.status_changed',
  'reunification.student.released',
  'reunification.student.arrived',
  'reunification.student.transported',
  'reunification.student.offsite_reported',
  // Guardian
  'reunification.guardian.arrived',
  'reunification.guardian.checked_in',
  'reunification.guardian.verified',
  'reunification.guardian.matched',
  'reunification.guardian.notified',
  'reunification.guardian.pre_verified',
  // Staff
  'reunification.staff.assigned',
  'reunification.staff.checked_in',
  'reunification.staff.reassigned',
  'reunification.staff.released',
  'reunification.staff.status_changed',
  'reunification.staff.unassigned',
  // Stats
  'reunification.stats.updated',
  // Alert-end (ADR 106) — originating alert ended, reunification continues
  'reunification.alert.ended',
] as const;

type CanonicalEventType = (typeof REUNIFICATION_EVENT_TYPES)[number];

/** Legacy bare-name aliases, each mapped to its canonical event type. */
const BACKEND_ALIASES: Record<string, CanonicalEventType> = {
  'reunification.activated': 'reunification.event.activated',
  'reunification.paused': 'reunification.event.paused',
  'reunification.resumed': 'reunification.event.resumed',
  'reunification.completed': 'reunification.event.completed',
  'student.status_changed': 'reunification.student.status_changed',
  'student.released': 'reunification.student.released',
  'student.arrived': 'reunification.student.arrived',
  'student.transported': 'reunification.student.transported',
  'student.offsite_reported': 'reunification.student.offsite_reported',
  'guardian.arrived': 'reunification.guardian.arrived',
  'guardian.checked_in': 'reunification.guardian.checked_in',
  'guardian.verified': 'reunification.guardian.verified',
  'guardian.matched': 'reunification.guardian.matched',
  'guardian.notified': 'reunification.guardian.notified',
  'guardian.pre_verified': 'reunification.guardian.pre_verified',
  'staff.assigned': 'reunification.staff.assigned',
  'staff.checked_in': 'reunification.staff.checked_in',
  'staff.reassigned': 'reunification.staff.reassigned',
  'staff.released': 'reunification.staff.released',
  'staff.status_changed': 'reunification.staff.status_changed',
  'staff.unassigned': 'reunification.staff.unassigned',
  'stats.updated': 'reunification.stats.updated',
};

type EventCategory =
  | 'event'
  | 'student'
  | 'guardian'
  | 'staff'
  | 'stats'
  | 'alert';

function categorize(eventType: CanonicalEventType): EventCategory {
  // Every entry in REUNIFICATION_EVENT_TYPES has `reunification.<category>.<verb>`
  // — typed so the call site doesn't need defensive null handling.
  const category = eventType.split('.')[1] as EventCategory;
  return category;
}

/**
 * Invalidate the right React Query keys for a reunification event payload.
 *
 * Event-list keys always invalidate so a reunification activated OUTSIDE the
 * current view is reflected immediately. Sub-resource keys (students,
 * guardians, staff, metrics) invalidate using a *prefix* match on the event
 * detail key so every active filter variant is refetched.
 *
 * We deliberately do NOT invalidate `reunificationSiteKeys` here — physical
 * site configuration does not change from event traffic, so invalidating it
 * on every student.status_changed caused unnecessary refetches.
 */
function invalidateForEvent(
  queryClient: HandlerDeps['queryClient'],
  category: EventCategory,
  eventId: string | null,
): void {
  // School-wide list is always invalidated — staff see new events appear.
  queryClient.invalidateQueries({ queryKey: reunificationEventKeys.all });

  if (!eventId) return;

  const detailKey = reunificationEventKeys.detail(eventId);
  queryClient.invalidateQueries({ queryKey: detailKey });

  switch (category) {
    case 'student':
      queryClient.invalidateQueries({ queryKey: [...detailKey, 'students'] });
      queryClient.invalidateQueries({ queryKey: [...detailKey, 'metrics'] });
      break;
    case 'guardian':
      queryClient.invalidateQueries({ queryKey: [...detailKey, 'guardians'] });
      queryClient.invalidateQueries({ queryKey: [...detailKey, 'metrics'] });
      break;
    case 'staff':
      queryClient.invalidateQueries({ queryKey: [...detailKey, 'staff'] });
      break;
    case 'stats':
      queryClient.invalidateQueries({ queryKey: [...detailKey, 'metrics'] });
      queryClient.invalidateQueries({ queryKey: [...detailKey, 'timeline'] });
      break;
    case 'alert':
      // ADR 106: originating alert ended but the reunification continues.
      // Just refresh the detail (which we already did above) so any UI
      // that surfaces the alert linkage reflects the transition.
      break;
    case 'event':
      // Lifecycle change — refresh every sub-resource for the event.
      queryClient.invalidateQueries({ queryKey: [...detailKey, 'students'] });
      queryClient.invalidateQueries({ queryKey: [...detailKey, 'guardians'] });
      queryClient.invalidateQueries({ queryKey: [...detailKey, 'staff'] });
      queryClient.invalidateQueries({ queryKey: [...detailKey, 'metrics'] });
      queryClient.invalidateQueries({ queryKey: [...detailKey, 'timeline'] });
      queryClient.invalidateQueries({ queryKey: [...detailKey, 'logs'] });
      break;
  }
}

/** Extract the event id from a broadcast payload (backend puts it at top-level). */
function extractEventId(
  data: Record<string, unknown>,
  raw?: unknown,
): string | null {
  const rawObj = raw as Record<string, unknown> | undefined;
  const fromRaw = rawObj?.event_id;
  const fromData = data?.event_id ?? data?.reunification_event_id;
  const candidate = typeof fromRaw === 'string' ? fromRaw : fromData;
  return typeof candidate === 'string' && candidate.length > 0 ? candidate : null;
}

function pickString(
  ...candidates: Array<unknown>
): string | undefined {
  for (const c of candidates) {
    if (typeof c === 'string' && c.length > 0) return c;
  }
  return undefined;
}

function pickNumber(...candidates: Array<unknown>): number | null {
  for (const c of candidates) {
    if (typeof c === 'number' && Number.isFinite(c)) return c;
    if (typeof c === 'string' && c.length > 0) {
      const n = Number(c);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

/**
 * Build the ReunificationActivatedPayload from a socket frame for the
 * Join prompt dispatcher. Returns null when `event_id` or `school_id`
 * are missing — the prompt needs both to switch orgs and deep-link.
 */
function buildActivatedPayload(
  data: Record<string, unknown>,
  raw?: Record<string, unknown>,
): ReunificationActivatedPayload | null {
  const eventId = extractEventId(data, raw);
  const schoolId = pickNumber(raw?.['school_id'], data['school_id']);
  if (!eventId || schoolId === null) return null;

  return {
    event_id: eventId,
    school_id: schoolId,
    school_name: pickString(data['school_name'], raw?.['school_name']),
    status: pickString(data['status']),
    is_drill: typeof data['is_drill'] === 'boolean' ? (data['is_drill'] as boolean) : undefined,
    incident_type: pickString(data['incident_type']),
    started_at: pickString(data['started_at']),
    activated_by: pickString(data['activated_by']),
  };
}

export function createReunificationHandlers(deps: HandlerDeps): SocketHandlerMap {
  const { queryClient, enqueueJoinPrompt } = deps;

  const handlerFor =
    (eventType: CanonicalEventType): SocketHandler =>
    (data, raw) => {
      const category = categorize(eventType);
      const eventId = extractEventId(data, raw);
      invalidateForEvent(queryClient, category, eventId);

      // Only `reunification.event.activated` drives the cross-site Join
      // prompt. Later lifecycle events (paused/resumed/completed) are
      // already reflected by query invalidation in the open drawer.
      if (eventType === 'reunification.event.activated' && enqueueJoinPrompt) {
        const rawObj = raw as Record<string, unknown> | undefined;
        const payload = buildActivatedPayload(data, rawObj);
        if (payload) enqueueJoinPrompt(payload);
      }
    };

  const map: SocketHandlerMap = {};

  for (const name of REUNIFICATION_EVENT_TYPES) {
    map[name] = handlerFor(name);
  }

  // Legacy compatibility: route bare backend names to the same handlers.
  for (const [alias, canonical] of Object.entries(BACKEND_ALIASES)) {
    map[alias] = map[canonical];
  }

  return map;
}
